import React, { useContext } from 'react'
import { Redirect, Route, useLocation } from 'react-router-dom'
import NativeAppLayout from '../../components/mainTabs/NativeAppLayout'
import WebAppLayout from '../../components/mainTabs/WebAppLayout'
import { AppContext } from '../../contexts/AppContext'
import useUser from '../../contexts/hooks/useUser'
import { UserContext } from '../../contexts/UserContext'
import Onboarding from '../../onboarding/Onboarding'
import Contact from '../contact/Contact'
import ContractInformation from '../contractInformation/ContractInformation'

import AccountRegistration from '../auth/AccountRegistration'
import ForgottenPassword from '../auth/ForgottenPassword'
import Login from '../auth/Login'
import GeneralContactPage from '../contact/generalContact/GeneralContactPage'
import InvoiceExtensionPage from '../contact/invoiceExtensionContact/InvoiceExtensionPage'
import MoveOutWizard from '../contact/moveOutWizard/MoveOutWizard'
import { ContactSupport } from '../contactSupport/ContactSupport'
import AddDevice from '../devices/addDevice/AddDevice'
import ElectricVehicleOnboarding from '../devices/addDevice/ElectricVehicleOnboarding'
import HvacOnboarding from '../devices/addDevice/HvacOnboarding'
import SolarPanelOnboarding from '../devices/addDevice/SolarPanelOnboarding'
import Devices from '../devices/Devices'
import ChargingLocations from '../devices/electricCar/chargingLocations/ChargingLocations'
import DownloadElectricCarChargingHistory from '../devices/electricCar/DownloadElectricCarChargingHistory/DownloadElectricCarChargingHistory'
import ElectricCarChargingHistory from '../devices/electricCar/electricCarChargingHistory/ElectricCarChargingHistory'
import ElectricCarDetail from '../devices/electricCar/ElectricCarDetail'
import ElectricCarSetup from '../devices/electricCarSetup/ElectricCarSetup'
import HvacDetail from '../devices/hvac/HvacDetail'
import SolarPanelDetail from '../devices/solarPanel/SolarPanelDetail'
import SolarPanelSettings from '../devices/solarPanel/solarPanelSettings/SolarPanelSettings'
import SolarPanelSetup from '../devices/solarPanel/solarPanelSetup/SolarPanelSetup'
import DownloadApp from '../downloadApp/DownloadApp'
import Electricity from '../electricity/Electricity'
import Overview from '../home/Home'
import Invoices from '../invoices/Invoices'
import MonthlyReportsList from '../monthlyReports/MonthlyReportsList'
import Notifications from '../notifications/Notifications'
import Saving from '../saving/Saving'
import AccountDeletion from '../settings/accountManagement/accountDeletion/AccountDeletion'
import AccountManagement from '../settings/accountManagement/AccountManagement'
import ChangePassword from '../settings/accountManagement/changePassword/ChangePassword'
import DeleteAccountConfirmation from '../settings/accountManagement/deleteAccountConfirmation/DeleteAccountConfirmation'
import AppSettings from '../settings/appSettings/AppSettings'
import Authorizations from '../settings/authorizations/Authorizations'
import MeteringPointInformation from '../settings/meteringPoint/meteringpointInformation/MeteringPointInformation'
import MeteringPointSettings from '../settings/meteringPoint/MeteringPointSettings'
import NotificationSettings from '../settings/notificationSettings/NotificationSettings'
import Settings from '../settings/Settings'
import AppStatusGuard from './AppStatusGuard'
import LoadingPage from './LoadingPage'
import ObjectSelector from './ObjectSelector'
import PermissionForm from './PermissionForm'
import ProtectedRoute from './ProtectedRoute'
import Register from './Register'
import Welcome from './Welcome'

const AppRouter: React.FC = () => {
	const { isMobileView } = useContext(AppContext)
	const { isUserContextLoading, isAnonymousUser } = useContext(UserContext)

	const { user } = useUser()
	const location = useLocation()

	if (isUserContextLoading) return <LoadingPage />

	const needsEmail = user && (!user?.email || !user.email.includes('@'))

	if (needsEmail && location.pathname !== '/register/create-user') {
		// Force email first
		return <Redirect to='/register/create-user' />
	}

	const Layout = isMobileView ? NativeAppLayout : WebAppLayout

	return (
		<AppStatusGuard>
			<Layout>
				<Route
					exact
					path='/'
					render={() =>
						user || isAnonymousUser ? (
							<Redirect to={user?.landingPage || '/overview'} />
						) : (
							<Welcome />
						)
					}
				/>
				<Route exact path='/consumption' render={() => <Redirect to='/exchange' />} />
				<Route exact path='/spotMargin' render={() => <Redirect to='/exchange/price/day' />} />
				<Route exact path='/register' component={Register} />
				<Route exact path='/register/authorize' component={Authorizations} />
				<Route exact path='/login' component={Login} />
				<Route exact path='/forgotten-password' component={ForgottenPassword} />
				<ProtectedRoute exact path='/overview' component={Overview} />
				<ProtectedRoute exact path='/register/create-user' component={AccountRegistration} />
				<ProtectedRoute exact path='/permissions' component={PermissionForm} />
				<ProtectedRoute path='/onboarding' component={Onboarding} />
				<ProtectedRoute exact path='/objectselector' component={ObjectSelector} />
				<ProtectedRoute exact path='/exchange' component={Electricity} />
				<ProtectedRoute path='/exchange/:element/:time' component={Electricity} />
				<ProtectedRoute exact path='/saving' component={Saving} />
				<ProtectedRoute exact path='/contracts' component={ContractInformation} />
				<ProtectedRoute exact path='/saving/:component' component={Saving} />
				<ProtectedRoute exact path='/consumption-elements' component={Devices} />
				<ProtectedRoute exact path='/devices' component={Devices} />

				<ProtectedRoute exact path='/contact-support' component={Contact} />
				<ProtectedRoute exact path='/settings' component={Settings} />
				<ProtectedRoute exact path='/settings/authorizations' component={Authorizations} />
				<ProtectedRoute exact path='/settings/metering-point' component={MeteringPointSettings} />
				<ProtectedRoute exact path='/settings/application' component={AppSettings} />
				<ProtectedRoute
					exact
					path='/settings/metering-point/information'
					component={MeteringPointInformation}
				/>

				<ProtectedRoute exact path='/settings/notifications' component={NotificationSettings} />
				<ProtectedRoute exact path='/settings/account-management' component={AccountManagement} />
				<ProtectedRoute
					exact
					path='/settings/account-management/change-password'
					component={ChangePassword}
				/>
				<ProtectedRoute
					exact
					path='/settings/account-management/delete-account'
					component={AccountDeletion}
				/>
				<ProtectedRoute
					exact
					path='/settings/account-management/delete-account-confirmation'
					component={DeleteAccountConfirmation}
				/>
				<ProtectedRoute exact path='/contact/move-out' component={MoveOutWizard} />
				<ProtectedRoute exact path='/contact/general' component={GeneralContactPage} />
				<ProtectedRoute exact path='/contact/invoice-extension' component={InvoiceExtensionPage} />
				<ProtectedRoute exact path='/support' component={ContactSupport} />
				<ProtectedRoute exact path='/application' component={DownloadApp} />
				<ProtectedRoute exact path='/reports' component={MonthlyReportsList} />
				<ProtectedRoute exact path='/invoices' component={Invoices} />
				<ProtectedRoute exact path='/contract' component={ContractInformation} />
				<ProtectedRoute
					exact
					path='/devices/electric-car/:vehicleId/charging-history'
					component={ElectricCarChargingHistory}
				/>
				<ProtectedRoute
					exact
					path='/devices/electric-car/:vehicleId/charging-statistics'
					component={DownloadElectricCarChargingHistory}
				/>
				<ProtectedRoute
					exact
					path='/devices/electric-car/:vehicleId/charging-locations'
					component={ChargingLocations}
				/>
				<ProtectedRoute
					exact
					path='/devices/electric-car/:vehicleId'
					component={ElectricCarDetail}
				/>
				<ProtectedRoute
					exact
					path='/devices/solar-panel/:inverterId'
					component={SolarPanelDetail}
				/>
				<ProtectedRoute exact path='/devices/solar-panel-settings' component={SolarPanelSettings} />
				<ProtectedRoute exact path='/devices/hvac/:hvacId' component={HvacDetail} />
				<ProtectedRoute exact path='/notifications' component={Notifications} />
				<ProtectedRoute exact path='/devices/add-device' component={AddDevice} />
				<ProtectedRoute
					exact
					path='/devices/add-device/electric-car'
					component={ElectricVehicleOnboarding}
				/>
				<ProtectedRoute
					exact
					path='/devices/add-device/electric-car/setup'
					component={ElectricCarSetup}
				/>
				<ProtectedRoute exact path='/devices/add-device/hvac' component={HvacOnboarding} />
				<ProtectedRoute
					exact
					path='/devices/add-device/solar-panel'
					component={SolarPanelOnboarding}
				/>
				<ProtectedRoute
					exact
					path='/devices/add-device/solar-panel/setup'
					component={SolarPanelSetup}
				/>

				{/* Fallback route for 404s */}
				<Route render={() => <Redirect to='/' />} />
			</Layout>
		</AppStatusGuard>
	)
}

export default AppRouter
